import { buildUrl } from '@agnivo/api-client';
import { isClientEventRoute, } from './clientTelemetryContract';
/**
 * ⛔ **Not collapsible to an identity pass, and this is the boundary's whole
 * point.** `'miniapp'` is the PLATFORM's wire vocabulary; `'telegram'` is
 * app-core's HOST enum. Returning `surface` unchanged would promote a host enum
 * into a wire contract — precisely what `Surface`'s own docstring exists to
 * prevent, since that enum carries two axes (Telegram-native provenance vs.
 * app-family shell) that the wire does not.
 *
 * It would also fail in the quiet direction: `'telegram'` is not on the
 * server-side allowlist, so the event is DROPPED and answered `204` like every
 * other outcome. No error reaches the client, nothing appears in the log, and the
 * surface simply stops reporting. Kept honest by a test asserting the rename, and
 * by a second one asserting it through the provider.
 *
 * ⛔ **A switch with a `never` arm, not a ternary, and for the same asymmetric
 * reason `writeFamily` gives (`config.ts`).** The ternary's implicit else meant
 * every FUTURE `Surface` member fell through to the wire unfiltered. A new
 * app-family surface would have been right by luck; a new Telegram-family shell
 * would have shipped its own host enum onto the wire, where the server drops it
 * silently and answers `204`. So the failure lands in the quiet direction and the
 * surface just stops reporting — with nothing red anywhere.
 *
 * The `never` arm makes that a COMPILE error instead: adding a `Surface` member
 * without deciding its wire token fails `typecheck` with
 * `TS2322: Type 'string' is not assignable to type 'never'`. Mutation-checked by
 * adding a fourth `Surface` member and confirming the red — a `never` arm that
 * still compiles with an unhandled case is decoration.
 *
 * ⛔ Do NOT collapse this back to a ternary.
 */
export function wireSurface(surface) {
    switch (surface) {
        case 'telegram':
            return 'miniapp';
        case 'web':
            return 'web';
        case 'desktop':
            return 'desktop';
        default: {
            const unreachable = surface;
            return unreachable;
        }
    }
}
export const CLIENT_EVENT_PATH = '/v1/telemetry/client-events';
/**
 * STRICT PER-TYPE PARTITION (architect AMENDMENT 3, 2026-08-30; the ordered-gate
 * correction of 2026-08-30T03:50Z quoted in `gateway!304`'s description). Replaces
 * the amended-2026-08-28 shared bucket (capacity 20 / refill 1/min) wholesale: one
 * tab no longer has ONE budget whose capacity a single loud type could monopolise
 * — each event type owns `{tokens, lastRefill, successHistory}` with its own
 * capacity and its own refill, so a `voice_playback_failed` storm cannot silence
 * a `render_failure` reporter (now wired) that has not emitted once.
 *
 * The refill remains the part that was argued for and won, so it remains the part
 * most worth guarding: a flat per-session cap spends itself in the first minutes of
 * a badly broken half-hour and then the tab is permanently silent — through the
 * recovery and through any *second*, different breakage.
 *
 * ⚠️ **The table is deliberately a SUPERSET of the wire send-set — now by
 * exactly one member.** `render_failure` has completed the architect's
 * ordering (contract type + gateway admission via `gateway!301` + the wired
 * producer, `web!515`), so only `unhandled_exception` still runs ahead: it has
 * a partition here while `gateway!304` (its admission) is open and it has no
 * contract type and no producer. The `WIRE_TYPES_ARE_BUDGETED` assertion below
 * remains the compile-time half of that deal — the wire vocabulary can never
 * grow a type this table lacks, while the table may run ahead of it. When
 * `unhandled_exception`'s producer lands, its type joins the contract and
 * finds its partition already enforced.
 *
 * `ability_row_disagreement` (Q4, architect ruling 2026-09-11) lands its contract type
 * and its producer together, so it needs a partition immediately, not ahead of one. Capacity
 * 4: the admin-tenant ability panel can disagree on at most 3 rows per mount today (vision,
 * plus the two-row scheduling pair) — one over that bound is headroom for a same-session
 * remount, not a guess at a larger population.
 */
export const CLIENT_EVENT_BUDGET = {
    ability_row_disagreement: 4,
    unhandled_exception: 6,
    render_failure: 7,
    voice_playback_failed: 7,
    // Capacity 10, sized from the window length rather than picked: at
    // `UMAMI_SEND_WINDOW_MS = 120_000` a session emits at most one window event per 2 min,
    // so an hour is 30 windows and the refill contributes `3600/180 = 20` — capacity 10
    // covers 30. I.e. FULL coverage for any session under ~54 min and ~93% across a full
    // hour, which is the point: a DENIED window event "leaves no trace" (see
    // `createEventBudget`), so a dropped window is indistinguishable from a window that
    // never closed. Coverage is therefore the thing to size for, and the per-type rate cap
    // (`PER_TYPE_LIMIT = 3` per 60s) is never the binding constraint at one per 2 min.
    //
    // ⚠️ A denial reduces COVERAGE, never ACCURACY: each summary carries its own
    // `sent/accepted/rejected/unobservable` for its own window, so a dropped window cannot
    // corrupt a neighbouring one's numbers. Stated because the arithmetic above makes drops
    // sound like undercounting, and they are not.
    umami_send_window: 10,
};
/**
 * Compile-time half of the budget-first ordering: if the wire vocabulary
 * (`ClientEventType`, derived from `CLIENT_TELEMETRY_CONTRACT`) ever grows a type
 * this table lacks, `ClientEventType` no longer extends `BudgetedEventType`, the
 * conditional resolves to `never`, and this assignment fails to compile. The
 * table running AHEAD of the wire (today: `unhandled_exception` only) is
 * the intended direction and type-checks fine.
 */
export const WIRE_TYPES_ARE_BUDGETED = true;
/** Refill is 1 token per type per interval, continuous (fractional tokens accumulate). */
export const REFILL_INTERVAL_MS = 180_000;
export const PER_TYPE_LIMIT = 3;
/** Retained from the 2026-08-28 amendment; now per-type, on the elapsed clock. */
export const PER_TYPE_WINDOW_MS = 60_000;
/**
 * Budget mechanics run on a MONOTONIC ELAPSED clock, deliberately not the wall
 * clock that `occurredAt` uses: an NTP correction mid-session that moves wall time
 * backwards must not refill the budget (or freeze it by making every interval
 * negative), and one that jumps it forwards must not grant an hour of tokens.
 * `performance.now()` is monotonic within a page's lifetime on every target; the
 * `Date.now` fallback (non-standard embedders) is handled by clamping the clock
 * the budget reads to its own high-water mark: a backwards excursion counts as no
 * time having passed for EVERY reader of `t` — refill, window pruning, and the
 * timestamp an admitted event records — and time resumes from the mark when the
 * clock catches up. (Clamping only the refill is not enough: timestamps written
 * at the rolled-back value age out relative to the LOWER clock while the clock is
 * still below the mark, silently re-opening a window that never earned the room.
 * Found by review counterexample, 2026-08-31, `web!513`.)
 */
function defaultElapsedClock() {
    const p = globalThis.performance;
    if (p && typeof p.now === 'function')
        return () => p.now();
    return Date.now;
}
export function createEventBudget(elapsedNow = defaultElapsedClock()) {
    const start = elapsedNow();
    // The partition-wide monotonic view of the elapsed clock (see
    // `defaultElapsedClock` above). One mark for the whole budget, not per
    // partition: all partitions read ONE clock, so an excursion rewinds time for
    // all of them equally — a per-partition mark would let a partition that has
    // not taken since creation refill straight through a rollback.
    let highWater = start;
    // Null prototype: no inherited keys, so the own-property check below cannot be
    // satisfied by anything the caller did not rule on.
    const partitions = Object.create(null);
    for (const key of Object.keys(CLIENT_EVENT_BUDGET)) {
        partitions[key] = { tokens: CLIENT_EVENT_BUDGET[key], lastRefill: start, successHistory: [] };
    }
    return {
        take(eventType) {
            // Fail-closed own-property lookup BEFORE any mutation: an unknown key (stale
            // bundle, untyped caller, prototype name) is denied without touching state.
            if (!Object.hasOwn(partitions, eventType))
                return false;
            const p = partitions[eventType];
            const raw = elapsedNow();
            if (raw > highWater)
                highWater = raw;
            const t = highWater;
            // Continuous refill — fractional tokens accumulate, so a burst of calls
            // milliseconds apart is not silently rounded up into free capacity, and a
            // DENIED poll still banks the elapsed time it observed.
            if (t > p.lastRefill) {
                p.tokens = Math.min(CLIENT_EVENT_BUDGET[eventType], p.tokens + (t - p.lastRefill) / REFILL_INTERVAL_MS);
                p.lastRefill = t;
            }
            const recent = p.successHistory.filter((ts) => t - ts < PER_TYPE_WINDOW_MS);
            p.successHistory = recent;
            // Per-type window is checked BEFORE the token so a type hammering past its
            // own sub-limit burns none of the tokens its quieter minutes depend on.
            if (recent.length >= PER_TYPE_LIMIT || p.tokens < 1)
                return false;
            p.tokens -= 1;
            recent.push(t);
            return true;
        },
    };
}
function randomId() {
    // `crypto.randomUUID` is secure-context only. dev and prod are https, but an
    // http:// origin (a plain-http preview, a LAN device) leaves it undefined and a
    // bare call would throw — from inside an error handler, which is the one place a
    // throw is least welcome. The value is a dedup key, not a security token.
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function')
        return c.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
/**
 * Defense in depth for JS callers and stale bundles: types reject undeclared routes, and this
 * runtime boundary drops one rather than letting the generated send-set under-report the wire.
 * An undefined or undeclared route is dropped like any undeclared value; a context left empty
 * by the drop is omitted entirely — architect round 4 (web!515): the old first branch returned
 * `{ route: undefined }` as-is, which serialised on the wire as `"context": {}` while the
 * docblock claimed the context was omitted.
 */
function wireContext(context) {
    if (!context)
        return context;
    const { route, ...rest } = context;
    if (route !== undefined && isClientEventRoute(route))
        return context;
    return Object.keys(rest).length > 0 ? rest : undefined;
}
/**
 * `apiMode !== 'live'` returns a no-op. A mock-mode shell has a mock auth
 * provider and no live session, so every report would be an unauthenticated POST
 * to the real API origin — 401s generated by the thing that exists to report
 * errors. The contract does not mention mock mode; app-core has one and it is
 * reachable from every shell's config.
 */
export function createClientEventReporter(cfg) {
    if (cfg.apiMode !== 'live')
        return () => { };
    const now = cfg.now ?? Date.now;
    const newId = cfg.newId ?? randomId;
    const budget = createEventBudget(cfg.elapsedNow);
    const surface = wireSurface(cfg.surface);
    // `buildUrl` is already in every shipped bundle (the transport uses it) and
    // already strips a trailing slash — a local regex here would be a second copy
    // of behaviour we ship anyway.
    const url = buildUrl(cfg.baseUrl, CLIENT_EVENT_PATH);
    return (event) => {
        if (!budget.take(event.eventType))
            return;
        // The whole body is inside try/catch: `getAccessToken` is shell-supplied and
        // may throw synchronously, and `fetch` throws on a malformed URL before it
        // ever returns a promise. Neither is a rejected promise `.catch` would see.
        try {
            const send = async () => {
                const headers = { 'Content-Type': 'application/json' };
                const token = cfg.getAccessToken ? await cfg.getAccessToken() : null;
                if (token)
                    headers.Authorization = `Bearer ${token}`;
                const context = wireContext(event.context);
                await (cfg.fetchImpl ?? fetch)(url, {
                    method: 'POST',
                    headers,
                    keepalive: true,
                    body: JSON.stringify({
                        eventType: event.eventType,
                        surface,
                        eventId: newId(),
                        occurredAt: new Date(now()).toISOString(),
                        errorName: event.errorName,
                        ...(event.errorMessage ? { errorMessage: event.errorMessage } : {}),
                        ...(context ? { context } : {}),
                    }),
                });
            };
            void send().catch(() => { });
        }
        catch {
            /* fire-and-forget: a reporter that can fail the caller is not one */
        }
    };
}
//# sourceMappingURL=clientEvent.js.map